/**
 * Verifies that a valid viem WalletClient is provided and properly configured.
 * Checks for required methods and connection status, optionally requesting address if needed.
 *
 * @param {import('viem').WalletClient} walletClient - The wallet client to verify
 * @param {Object} [options] - Verification options
 * @param {boolean} [options.requestAddress=true] - Whether to request wallet address if not available
 *
 * @returns {Promise<import('viem').WalletClient>} Verified wallet client, potentially with updated address
 *
 * @throws {Error} If no wallet client is provided
 * @throws {Error} If client lacks writeContract method
 * @throws {Error} If wallet is not connected and address request fails/is disabled
 *
 * @example
 * // Example 1: Basic verification
 * const verifiedClient = await verifyWalletClient(walletClient);
 *
 * @example
 * // Example 2: Verify without requesting address
 * const verifiedClient = await verifyWalletClient(walletClient, {
 *   requestAddress: false
 * });
 */
export const verifyWalletClient = async (
  walletClient,
  options = { requestAddress: true }
) => {
  if (!walletClient) {
    throw new Error(`Verifiy Wallet Client: No wallet client provided!`);
  }

  if (
    !walletClient?.writeContract ||
    typeof walletClient.writeContract !== "function"
  )
    throw new Error(
      "Error verifying wallet client: Client does not have `writeContract` method. Make sure you are using Viem WalletClient."
    );

  if (!walletClient?.account || !walletClient?.account?.address) {
    if (options.requestAddress) {
      const [address] = await walletClient.getAddresses();
      const newWalletClient = (walletClient.address = address);
      return newWalletClient;
    }

    throw new Error("Verifiy Wallet Client: Wallet is not connected!");
  }
  return walletClient;
};
